<template>
  <BaseModal :title="modalTitle">
    <template slot="header-action">
      <a
        :href="SUPPORT_LINK"
        class="link text-sm grey-500 spacex-8 offer-help-button"
        target="_blank"
      >
        <HelpIcon class="icon-16px" />
        <span>
          {{ $t('I need help') }}
        </span>
      </a>
    </template>
    <div class="spacey-8">
      <div v-if="!createdVersionId" class="d-contents spacey-8">
        <button type="button" class="card-1 card-1__button" @click="createNewOffer">
          <div class="card-1__info">
            <Spinner v-if="draftCreating" class="card-1__spinner" size="16" />
            <PlusIcon v-else class="icon-14px" />
            <div class="spacey-4">
              <div class="text-bs black">
                {{ $t('Use Fixtender Sheets') }}
              </div>
              <div class="text-bst grey-500">
                {{
                  $t(
                    'Use our spreadsheet tool to quickly enter prices and send them to the customer'
                  )
                }}
              </div>
            </div>
          </div>
        </button>
        <div class="text-bs black break-blocks">
          {{ $t('or') }}
        </div>
      </div>
      <div class="card-1 import-card spacey-20">
        <div class="card-1__info">
          <FileIcon class="grey-500-icon icon-20px" />
          <div class="spacey-4">
            <div class="text-bs black">
              {{ $t('Import file from Excel template') }}
            </div>
            <div class="text-bst grey-500">
              {{ $t('Use our Excel template to fill in the prices. Then import it below.') }}
            </div>
          </div>
        </div>
        <div class="spacey-8">
          <div class="text-sm grey-900">1. {{ $t('Download our up-to-date Excel template') }}:</div>
          <Button size="small" :loading="templateIsDownloading" @click="downloadTemplate">
            <DownloadIcon class="button-icon" />{{ $t('Download template') }}
          </Button>
        </div>
        <div class="spacey-8">
          <!--          TODO: read our guide-->
          <div class="text-sm grey-900">
            2. {{ $t("Fill out the template. If you're having trouble") }},
            <a :href="SUPPORT_LINK" target="_blank" class="link">
              {{ $t('read the guide') }}
            </a>
            {{ $t('or') }}
            <a :href="SUPPORT_LINK" target="_blank" class="link">
              {{ $t('contact our support') }} </a
            >:
          </div>
          <Button size="small" @click="openGuide">
            <BurgerIcon class="button-icon" />{{ $t('Read our guide') }}
          </Button>
        </div>
        <div class="spacey-8">
          <div class="text-sm grey-900">
            3.
            {{ $t('Upload Excel template below. You will be able to add more documents later') }}.
          </div>
          <div v-if="!createdVersionId" class="offer-error spacex-12">
            <ErrorIcon class="warning-icon" />
            <div class="text-sm">
              {{
                $t(
                  'Once uploaded, a draft of the offer will be created. You will be able to edit prices, add descriptions and additional documents. The customer will not see your offer until you confirm the submission at a later date.'
                )
              }}
            </div>
          </div>
          <FileAttachment
            class="offer-attach-files"
            :files="files"
            :default-label="false"
            @file-input="handleFileInput"
            @file-delete="handleFileDelete"
          />
          <div v-if="importError" class="offer-attach-files__error">
            <Badge color="danger"> ! </Badge>
            <div class="offer-attach-files__error-info">
              <div class="text-bsb grey-900">
                {{ $t('Resolve an error in the file:') }}
              </div>
              <span class="grey-900 text-sm">{{ errorText }}</span>
            </div>
          </div>
        </div>
        <div class="import-button-container">
          <SuccessMessage v-if="isFinished">
            {{
              $t(
                'Done! Your file will be processed in a few minutes. We will let you know by email.'
              )
            }}
          </SuccessMessage>
          <InfoTooltip v-else top>
            <template #element>
              <Button
                :type="disableImport && 'ghost'"
                :disabled="disableImport"
                :loading="fileValidation || importing"
                @click="handleImport"
              >
                {{ $t('Import') }}
              </Button>
            </template>
            <template v-if="disableImport" #default>
              <span>{{ $t('Upload a file to proceed') }}</span>
            </template>
          </InfoTooltip>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
  import PlusIcon from '@/assets/icons/plus.svg';
  import FileIcon from '@/assets/icons/file.svg';
  import Button from '@/components/ui/Button';
  import DownloadIcon from '@/assets/icons/download-2.svg';
  import BurgerIcon from '@/assets/icons/burger-dots.svg';
  import ErrorIcon from '@/assets/icons/error-red.svg';
  import FileAttachment from '@/components/projects/FileAttachment';
  import { mapActions, mapGetters, mapState } from 'vuex';
  import offerTemplate from '@/utils/mixins/offer-template';
  import factory from '@/utils/api/factory';
  import Badge from '@/components/ui/Badge';
  import get from 'lodash.get';
  import Spinner from 'vue-simple-spinner';
  import BaseModal from '@/components/ui/BaseModal';
  import { SUPPORT_LINK } from '@/utils/constants/api';
  import HelpIcon from '@/assets/icons/help.svg';
  import SuccessMessage from '@/components/ui/SuccessMessage';
  import InfoTooltip from '@/components/ui/InfoTooltip';

  export default {
    name: 'CreateOfferModal',
    components: {
      SuccessMessage,
      PlusIcon,
      FileIcon,
      Button,
      DownloadIcon,
      BurgerIcon,
      ErrorIcon,
      FileAttachment,
      Badge,
      Spinner,
      BaseModal,
      HelpIcon,
      InfoTooltip,
    },
    mixins: [offerTemplate],
    data() {
      return {
        files: [],
        importError: null,
        fileValidation: false,
        importing: false,
        draftCreating: false,
        isFinished: false,
        SUPPORT_LINK,
      };
    },
    computed: {
      ...mapGetters('incomingTender', ['offerCanBeAdded']),
      ...mapState('incomingTender', [
        'incomingTenderInfo',
        'incomingTenderVersions',
        'exportXlsxErrorText',
      ]),
      ...mapState('modal', ['context']),
      offerId() {
        return this.incomingTenderInfo?.id;
      },
      disableImport() {
        return this.importError || !this.files.length || this.fileValidation;
      },
      createdVersionId() {
        return get(this.context, 'versionId', null);
      },
      errorText() {
        return this.exportXlsxErrorText || this.importError;
      },
      modalTitle() {
        return this.createdVersionId ? this.$t('Edit offer') : this.$t('Create offer');
      },
    },
    methods: {
      ...mapActions('incomingTender', [
        'createIncomingTenderVersion',
        'loadIncomingTenderInfo',
        'uploadXlsxTemplate',
      ]),
      ...mapActions('modal', ['closeModal']),
      async handleFileInput(files) {
        this.files = [files[0]];
      },
      handleFileDelete() {
        this.files = [];
        this.importError = null;
      },
      async createNewOffer(closeModal = true) {
        this.draftCreating = true;
        try {
          if (this.offerCanBeAdded) {
            await this.createIncomingTenderVersion(this.offerId);
          } else {
            await factory('incoming_tenders/offers').postInResourcePath(
              this.offerId,
              'participate',
              {
                currency: this.context.currency,
              }
            );
          }
          await this.loadIncomingTenderInfo(this.offerId);
          if (closeModal) {
            this.closeModal();
          }
          await this.$router.push({ name: 'incoming-tender-offers' });
        } catch (e) {
          console.error(e);
        }
        this.draftCreating = false;
      },
      downloadTemplate() {
        // YYYY-MM-DD
        const currentDate = new Date().toISOString().slice(0, 10);
        const fileName = `${this.incomingTenderInfo?.tender?.name || ''}-${currentDate}.xlsx`;
        this.loadTemplate(this.incomingTenderInfo.id, fileName, this.context.currency);
      },
      async handleImport() {
        this.importing = true;
        try {
          if (this.createdVersionId) {
            await this.uploadXlsxTemplate({
              incomingTenderId: this.offerId,
              file: this.files[0],
              offerVersionId: this.createdVersionId,
            });
          } else {
            await this.createNewOffer(false);

            await this.uploadXlsxTemplate({
              incomingTenderId: this.offerId,
              file: this.files[0],
              offerVersionId: this.incomingTenderVersions[0]?.id || null,
            });
          }
        } catch (e) {
          console.error(e);
        }
        this.importing = false;
        this.isFinished = true;
      },
      openGuide() {
        window.open(this.SUPPORT_LINK, '_blank').focus();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .card-1 {
    padding: 12px 20px;

    background: $grey50;
    border: 1px solid $grey100;
    box-sizing: border-box;
    border-radius: 8px;

    &__info {
      display: grid;
      grid-template-columns: 30px 1fr;
      align-items: center;
    }
  }

  .modal-content__container {
    top: 54px;
    bottom: 0;
    padding: 20px 24px;
  }

  .break-blocks {
    text-align: center;
  }

  .button-icon {
    margin-right: 4px;
  }

  .offer-error {
    display: flex;
    align-items: center;

    padding: 8px 16px;
    background: #fff3d8;
    box-shadow: inset 0px -1px 0px #e8ebf0;
    border-radius: 4px;

    .warning-icon {
      min-width: 20px;
      height: auto;
    }
  }

  .import-card {
    padding: 12px 20px 20px;
  }

  .offer-attach-files {
    margin-bottom: 0 !important;

    &__error {
      display: grid;
      grid-template-columns: 20px 1fr max-content;
      grid-column-gap: 8px;

      padding: 12px 16px;
      background: #ffffff;
      border: 1px solid #e8ebf0;
      border-radius: 8px;
    }

    &__error-button {
      margin: auto;
    }
  }

  .import-button-container {
    width: fit-content;
    margin-left: auto;
    margin-right: 0;
  }

  .card-1__button {
    width: 100%;
    text-align: start;
  }

  .card-1__spinner {
    width: fit-content;
  }

  .offer-help-button {
    display: flex;
    align-items: center;
  }
</style>
